import * as React from 'react';
import Panel from '../../panel/Panel';
import { observer } from 'mobx-react-lite';
import { useTranslation } from "react-i18next";
import { useStores } from '../../../stores';
import ScheduleAnalysis from '../../../domain/ScheduleAnalysis';
import moment from 'moment';
import ScheduleAnalysisToolbar from './ScheduleAnalysisToolbar';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import { Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs } from '@mui/material';

const ScheduleSchoolAnalysisPage = observer(() => {
    const { t } = useTranslation();
    const { scheduleStore } = useStores();
    const [from, setFrom] = React.useState(moment().startOf("month").startOf("week").toDate());
    const [to, setTo] = React.useState(moment().startOf("week").toDate());
    const [sort, setSort] = React.useState("default");
    const [analysis, setAnalysis] = React.useState<ScheduleAnalysis | undefined>(undefined);
    const [roomTab, setRoomTab] = React.useState(0);

    React.useEffect(() => {
        scheduleStore.getAnalysis(from, to, sort, (analysisResult) => {
            setAnalysis(analysisResult);
        });
    }, [from, to, sort, scheduleStore]);

    React.useEffect(() => {
        if (!analysis || roomTab < analysis.school.table.length) {
            return;
        }
        setRoomTab(0);
    }, [analysis, roomTab]);

    const updateDates = (fromDate: Date, toDate: Date) => {
        setFrom(fromDate);
        setTo(toDate);
    }

    const updateSort = (sortValue: string) => {
        setSort(sortValue);
    }

    const handleRoomTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setRoomTab(newValue);
    };

    return (
        <Panel
            title={t("schedule_analysis")}
            headerItems={[]}>
            <ScheduleAnalysisToolbar
                from={from}
                to={to}
                sort={sort}
                onRangeChange={updateDates}
                onSortChange={updateSort}
            />
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <TableContainer>
                        <Table size="medium">
                            <TableHead>
                                <TableRow>
                                    {analysis && (
                                        <>
                                            <TableCell align="center"><h3>{t("Student")}</h3></TableCell>
                                            <TableCell align="center"><h3>{t("Assistant")}</h3></TableCell>
                                            <TableCell align="center"><h3>{t("BibleReading")}</h3></TableCell>
                                            <TableCell align="center"><h3>{t("Conversation")}</h3></TableCell>
                                            <TableCell align="center"><h3>{t("BibleStudy")}</h3></TableCell>
                                            <TableCell align="center"><h3>{t("Talk")}</h3></TableCell>
                                        </>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    {analysis && (
                                        <>
                                            <TableCell align="center"><h1>{analysis.school.summary.students}</h1></TableCell>
                                            <TableCell align="center"><h1>{analysis.school.summary.assistants}</h1></TableCell>
                                            <TableCell align="center"><h1>{analysis.school.summary.bibleReading}</h1></TableCell>
                                            <TableCell align="center"><h1>{analysis.school.summary.conversation}</h1></TableCell>
                                            <TableCell align="center"><h1>{analysis.school.summary.bibleStudy}</h1></TableCell>
                                            <TableCell align="center"><h1>{analysis.school.summary.talk}</h1></TableCell>
                                        </>
                                    )}
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Tabs value={roomTab} onChange={handleRoomTabChange} variant="scrollable" scrollButtons="auto">
                        {analysis && analysis.school.table.map(room => <Tab key={room.name} label={room.name} />)}
                    </Tabs>
                    {analysis && analysis.school.table.map((room, index) => (
                        <div key={room.name} role="tabpanel" hidden={roomTab !== index}>
                            <TableContainer>
                                <Table size="medium">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t("surname")}</TableCell>
                                            <TableCell>{t("name")}</TableCell>
                                            <TableCell>{t("total")}</TableCell>
                                            <TableCell>{t("Conversation")}</TableCell>
                                            <TableCell>{t("BibleStudy")}</TableCell>
                                            <TableCell>{t("BibleReading")}</TableCell>
                                            <TableCell>{t("Talk")}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {room.rows.map(row => (
                                            <TableRow key={row.id}>
                                                <TableCell>{row.lastName}</TableCell>
                                                <TableCell>{row.firstName}</TableCell>
                                                <TableCell>{row.total}</TableCell>
                                                <TableCell>{row.conversation}</TableCell>
                                                <TableCell>{row.bibleStudy}</TableCell>
                                                <TableCell>{row.bibleReading}</TableCell>
                                                <TableCell>{row.talk}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    ))}
                </Paper>
            </Container>
        </Panel>
    );
});

export default ScheduleSchoolAnalysisPage;